import { useCallback, useEffect, useState } from "react";
import { DollarSign, X } from "react-feather";
import {
  paymentMethodsService,
  PayoutReadiness,
  PayoutReadinessProgram,
} from "../../services/paymentMethods.service";
import { formatCurrency } from "../../utils/formatters";
import { Button } from "../ui/button";
import { PayoutMethodModal } from "./PayoutMethodModal";
import { PaymentMethodData } from "./PaymentInfoForm";
import { useAffiliate } from "../../contexts/affiliate";

const DISMISS_KEY = "affiliate_payout_method_banner_dismissed";

function readDismissed(): boolean {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * The first-earning nudge. Shows once money is waiting in a program and no
 * saved payout method can receive it. Opens the inline add-method form
 * filtered to that brand's methods; disappears the moment a method exists.
 * Dismissible for the session — it is a nudge, never a wall.
 */
export function PayoutMethodBanner() {
  const { details } = useAffiliate();
  const [readiness, setReadiness] = useState<PayoutReadiness | null>(null);
  const [dismissed, setDismissed] = useState(readDismissed);
  const [target, setTarget] = useState<PayoutReadinessProgram | null>(null);
  const [initialData, setInitialData] = useState<PaymentMethodData[]>([]);

  const load = useCallback(async () => {
    try {
      const data = await paymentMethodsService.getPayoutReadiness();
      setReadiness(data);
    } catch (error) {
      // The banner is decoration on top of the dashboard; a failed fetch
      // simply means no banner.
      console.error("Failed to load payout readiness:", error);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const needs = readiness?.needsAttention ?? [];
  if (dismissed || needs.length === 0) return null;

  const first = needs[0];
  const total = needs.reduce((sum, p) => sum + p.available, 0);
  const brandLabel =
    needs.length === 1
      ? first.brandName
      : `${first.brandName} and ${needs.length - 1} other brand${needs.length > 2 ? "s" : ""}`;

  const openFor = async (program: PayoutReadinessProgram) => {
    // Pre-fill with what is already saved so an existing method (one this
    // brand happens not to accept) is not wiped by the save.
    if (details?.id) {
      try {
        const info = await paymentMethodsService.getAffiliatePaymentInfo(details.id);
        setInitialData(
          info.map((i) => ({
            paymentMethodId: i.paymentMethodId,
            fieldValues: Object.entries(i.fieldData).map(([fieldName, value]) => ({
              fieldName,
              value,
            })),
          }))
        );
      } catch {
        setInitialData([]);
      }
    }
    setTarget(program);
  };

  const dismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(DISMISS_KEY, "1");
    } catch {
      // ignore
    }
  };

  return (
    <>
      <div
        className="mb-6 flex flex-col gap-3 rounded-xl border border-brand-600/20 bg-brand-600/5 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
        role="status"
        data-testid="payout-method-banner"
      >
        <div className="flex items-start gap-3">
          <DollarSign className="mt-0.5 shrink-0 text-brand-600" size={18} />
          <div>
            <p className="text-sm font-medium text-foreground">
              You've earned {formatCurrency(total)} from {brandLabel}. Tell us where to send it.
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {first.supportedPaymentMethods.length > 0
                ? `${first.brandName} pays with ${first.supportedPaymentMethods
                    .map((m) => m.name)
                    .join(", ")}. Add one and you're ready for your first payout.`
                : "Add a payout method and you're ready for your first payout."}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 sm:shrink-0">
          <Button size="sm" onClick={() => openFor(first)}>
            Add payout method
          </Button>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss"
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      <PayoutMethodModal
        isOpen={!!target}
        onClose={() => setTarget(null)}
        initialData={initialData}
        allowedMethodNames={target?.supportedPaymentMethods.map((m) => m.name) ?? []}
        onSave={(methods) =>
          paymentMethodsService.bulkUpdatePaymentMethod(methods).then(() => undefined)
        }
        onSaved={load}
        saveLabel="Save payout method"
      />
    </>
  );
}
